import { Injectable, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { GoogleAnalyticsService } from 'ngx-google-analytics';
import { filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { DataService } from '../data/data.service';
import { environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root'
})
export class AnalyticsService implements OnDestroy {
  private idAnalytics: string = '';
  private currentUser: string | null = null;
  private destroyed$ = new Subject<void>();
  private isInitialized = false;

  constructor(
    private readonly router: Router,
    private gaService: GoogleAnalyticsService,
    private readonly dataService: DataService,
  ) {}

  startService(idAnalytics: string) {
    if (!environment.production) {
      return;
    }

    if (this.isInitialized) {
      return;
    }

    this.idAnalytics = idAnalytics;
    this.isInitialized = true;

    this.gaService.gtag('config', this.idAnalytics, {
      send_page_view: false
    });

    this.trackPageViews();

    if (this.dataService && this.dataService.id) {
      this.setUserId(this.dataService.id, {
        user_properties: {
          role: this.dataService.role,
          email: this.dataService.user
        }
      });
    }
  }

  private trackPageViews() {
    this.router.events
    .pipe(
      filter(event => event instanceof NavigationEnd),
      takeUntil(this.destroyed$)
    )
    .subscribe((event: NavigationEnd) => {
      this.pageView(event.urlAfterRedirects);
    });
  }

  private pageView(url: string) {
    if (!this.isInitialized || !environment.production) return;

    const pageViewParams: any = {
      page_title: document.title,
      page_location: window.location.origin + url,
      page_path: url
    };

    if (this.currentUser) {
      pageViewParams.user_id = this.currentUser;
    }

    this.gaService.pageView(url, pageViewParams);
  }

  setUserId(userId: string, userData: any = {}) {
    if (!this.isInitialized || !environment.production) return;

    this.currentUser = userId;

    this.gaService.gtag('set', { 'user_id': userId });

    if (userData?.user_properties) {
      this.gaService.gtag('set', 'user_properties', userData.user_properties);
    }
  }

  clearUserId() {
    if (!this.isInitialized || !environment.production) return;

    this.gaService.gtag('config', this.idAnalytics, {
      'user_id': null
    });

    this.event('logout');
    this.currentUser = null;
  }

  event(eventName: string, params: any = {}) {
    if (!this.isInitialized || !environment.production) return;

    if (this.currentUser && !params.user_id) {
      params.user_id = this.currentUser;
    }

    this.gaService.event(eventName, params);
  }

  ngOnDestroy() {
    this.destroyed$.next();
    this.destroyed$.complete();
  }
}